import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Building2, Users, Building, Activity, CheckCircle } from 'lucide-react';
import StatusBadge from '../../components/common/StatusBadge';
import Tabs, { type Tab } from '../../components/common/Tabs';
import CompanyModal from '../../components/admin/CompanyModal';
import DataTable from '../../components/common/DataTable';
import type { Column } from '../../components/common/DataTable';
import { companyService } from '../../services';
import type { Company } from '../../services';
import { useToast } from '../../components/common/ToastContainer';

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchCompany = async () => {
      try {
        setLoading(true);
        const companyData = await companyService.getById(id);
        setCompany(companyData);
      } catch (err: any) {
        setError(err.message || 'Failed to load company details');
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [id]);

  const handleSaveCompany = async (updatedCompany: any) => {
    if (!id) return;
    try {
      await companyService.update(id, {
        name: updatedCompany.name,
        address: updatedCompany.address,
        contact: updatedCompany.contact,
        phone: updatedCompany.phone,
        email: updatedCompany.email,
        description: updatedCompany.description,
      });
      const companyData = await companyService.getById(id);
      setCompany(companyData);
      setIsEditModalOpen(false);
    } catch (err: any) {
      toast.error('Update Failed', `Failed to update company: ${err.message}`);
    }
  };

  const companyForModal = company ? {
    id: company.id,
    name: company.name,
    address: company.address || '',
    contact: company.contact || '',
    phone: company.phone || '',
    email: company.email || '',
    description: '',
  } : undefined;

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading company details...</div>;
  }

  if (error || !company) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">{error || 'Company not found'}</div>
      </div>
    );
  }

  // Tab 1: Overview
  const OverviewTab = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Company Name</label>
              <p className="text-gray-900">{company.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Email</label>
              <p className="text-gray-900">{company.email || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Phone</label>
              <p className="text-gray-900">{company.phone || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Address</label>
              <p className="text-gray-900">{company.address || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Timestamps</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-500">Created</label>
              <p className="text-gray-900">{company.createTime || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Last Updated</label>
              <p className="text-gray-900">{company.updateTime || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Created By</label>
              <p className="text-gray-900">{company.createBy || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Tab 2: Tenants
  // TODO: Connect to tenants API endpoint when available
  const mockTenants = [
    { id: '1', name: 'Main Office', domain: 'main.example.com', users: 25, status: 'Active' },
    { id: '2', name: 'Branch A', domain: 'branch-a.example.com', users: 12, status: 'Active' },
    { id: '3', name: 'Branch B', domain: 'branch-b.example.com', users: 8, status: 'Active' },
  ];

  const tenantColumns: Column<typeof mockTenants[0]>[] = [
    { key: 'name', label: 'Tenant Name', sortable: true },
    { key: 'domain', label: 'Domain', sortable: true },
    { key: 'users', label: 'Users', sortable: true },
    { key: 'status', label: 'Status', sortable: true, render: (t) => <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">{t.status}</span> },
  ];

  const TenantsTab = () => (
    <div>
      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
        <strong>Note:</strong> Tenant data is currently mock data. TODO: Connect to tenants API endpoint filtered by company_id when available.
      </div>
      <DataTable columns={tenantColumns} data={mockTenants} searchPlaceholder="Search tenants..." />
    </div>
  );

  // Tab 3: Users
  // TODO: Connect to users API endpoint when available
  const mockUsers = [
    { id: '1', name: 'John Doe', email: 'john@example.com', role: 'Admin', status: 'Active' },
    { id: '2', name: 'Jane Smith', email: 'jane@example.com', role: 'User', status: 'Active' },
    { id: '3', name: 'Bob Johnson', email: 'bob@example.com', role: 'User', status: 'Inactive' },
  ];

  const userColumns: Column<typeof mockUsers[0]>[] = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'role', label: 'Role', sortable: true },
    { key: 'status', label: 'Status', sortable: true, render: (u) => <span className={`px-2 py-1 text-xs font-medium rounded-full ${u.status === 'Active' ? 'bg-success-100 text-success-700' : 'bg-gray-100 text-gray-700'}`}>{u.status}</span> },
  ];

  const UsersTab = () => (
    <div>
      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
        <strong>Note:</strong> User data is currently mock data. TODO: Connect to users API endpoint filtered by company_id when available.
      </div>
      <DataTable columns={userColumns} data={mockUsers} searchPlaceholder="Search users..." />
    </div>
  );

  // Tab 4: Activity
  // TODO: Connect to activity log API endpoint when available
  const mockActivity = [
    { id: '1', timestamp: '2025-12-18 10:30', action: 'Update', user: 'admin@example.com', details: 'Updated company information', status: 'Success' },
    { id: '2', timestamp: '2025-12-18 09:15', action: 'Create', user: 'admin@example.com', details: 'Created new tenant "Branch A"', status: 'Success' },
    { id: '3', timestamp: '2025-12-17 16:20', action: 'Update', user: 'admin@example.com', details: 'Updated contact information', status: 'Success' },
  ];

  const activityColumns: Column<typeof mockActivity[0]>[] = [
    { key: 'timestamp', label: 'Timestamp', sortable: true },
    { key: 'action', label: 'Action', sortable: true, render: (a) => <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">{a.action}</span> },
    { key: 'user', label: 'User', sortable: true },
    { key: 'details', label: 'Details', sortable: false },
    { key: 'status', label: 'Status', sortable: true, render: (a) => <span className="inline-flex items-center space-x-1 text-success-700"><CheckCircle className="w-4 h-4" /><span>{a.status}</span></span> },
  ];

  const ActivityTab = () => (
    <div>
      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
        <strong>Note:</strong> Activity data is currently mock data. TODO: Connect to activity log API endpoint when available.
      </div>
      <DataTable columns={activityColumns} data={mockActivity} searchPlaceholder="Search activity..." />
    </div>
  );

  const tabs: Tab[] = [
    { id: 'overview', label: 'Overview', icon: Building2, content: <OverviewTab key="overview" /> },
    { id: 'tenants', label: 'Tenants', icon: Building, content: <TenantsTab key="tenants" /> },
    { id: 'users', label: 'Users', icon: Users, content: <UsersTab key="users" /> },
    { id: 'activity', label: 'Activity', icon: Activity, content: <ActivityTab key="activity" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/companies')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{company.name}</h1>
            <p className="text-gray-600 mt-1">{company.email || 'No email'} • {company.phone || 'No phone'}</p>
          </div>
          <StatusBadge status="Active" />
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={() => setIsEditModalOpen(true)} className="btn-primary inline-flex items-center space-x-2">
            <Edit className="w-4 h-4" />
            <span>Edit Info</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <Tabs tabs={tabs} />
      </div>

      {/* Edit Company Modal */}
      {companyForModal && (
        <CompanyModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveCompany}
          company={companyForModal}
          mode="edit"
        />
      )}
    </div>
  );
};

export default CompanyDetail;
